<template>
    <div class="section-container w-100 mt-4">
        <SectionTitle title="Longitudinal Peak Analysis">
            <MoreInfo :id="'longitudinal-peak-demands-more-info'" title="Longitudinal Peak Analysis">
                <h4 class="font-weight-bold">Peak Physical Capacity Capacity Over Time</h4>
                <ul class="longitudinal-analysis-list">
                    <li>
                        Peak Physical Capacity Over Time displays each player's average peak periods longitudinally
                        compared to
                        the {{ referenceText }} data.
                    </li>
                    <slot name="shared-info" />
                </ul>
                <h4 class="font-weight-bold">Peak Threshold Count Over Time</h4>
                <ul class="longitudinal-analysis-list">
                    <li>
                        Peak Threshold Count Over Time displays each player's average peak periods longitudinally
                        compared to the
                        {{ referenceText }} data.
                    </li>
                    <slot name="shared-info" />
                </ul>
            </MoreInfo>
        </SectionTitle>

        <div class="d-flex justify-content-center my-4 mx-auto">
            <RadioButton class="mx-4" :id="'kpi-radio-group'" :modal="kpiSelected" :options="kpiOptions"
                :name="'kpi-epoch-radio-options'" @update-modal="updateKpi" :task="updatePeakCharts" />
            <RadioButton :id="'epoch-radio-group'" :modal="epochSelected" :options="epochOptions"
                :name="'epoch-radio-options'" @update-modal="epochSelected = $event" :task="updatePeakCharts" />
        </div>

        <div class="row justify-content-evenly mx-auto mt-4">
            <div class="col-12 col-lg-6">
                <h4 class="w-100 text-center">Peak Physical Capacity Over Time</h4>
                <MissingDataText v-if="!hasPeakEpochData" message="Insufficient data for player" />
                <div v-else>
                    <div class="d-flex align-items-center mx-auto my-4" style="height: 5vh; width: 40vw;">
                        <slot name="rainbow-legend" />
                        <RainbowLegend class="w-100 w-lg-75 mx-auto" style="margin-top:2vh" :showBlueKey="false"
                            :keyText="positionReferenceText" />
                    </div>
                    <LongitudinalChart :id="epochSelected + '-peak-chart-capacity-id'"
                        :title="selectedKpiTitle + ' ' + title" :data="rainbowDataByKpisByEpoch" />
                </div>
            </div>
            <div class="col-12 col-lg-6 mt-4 mt-lg-0">
                <h4 class="w-100 text-center">Peak Threshold Count Over Time</h4>
                <MissingDataText v-if="!hasPeakEpochCountData" message="Insufficient data for player" />
                <div v-else>
                    <div class="d-flex align-items-center mx-auto my-4" style="height: 5vh;">
                        <slot name="peak-counts-legend" />
                    </div>
                    <LongitudinalChart :id="epochSelected + '-peak-count-capacity-id'"
                        :title="selectedKpiTitle + ' ' + title" :data="peakEpochCountsByKpisByEpoch" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { convertChart, convertRainbowChart, mapKpiCategory, generateKpiEpochData } from '@/utils/LongitudinalChartHelper';
import MissingDataText from './MissingDataText.vue';
import RadioButton from './RadioButton.vue';
import SectionTitle from './SectionTitle.vue';
import MoreInfo from './MoreInfo.vue';
import RainbowLegend from './RainbowLegend.vue';
import LongitudinalChart from './LongitudinalChart.vue';
import Colours from '@/utils/Colours';
import KpiOption from "@/utils/KpiOption";
import { MeasurementTypes, getMeasurementUnit } from '@/utils/MeasurementSystem';

export default {
    props: {
        sessions: Array,
        rainbowData: Object,
        rainbowComparisonData: Object,
        peakEpochCountData: Object,
        peakEpochCountComparisonData: Object,
        referenceText: {
            type: String,
            default: 'All Competition'
        },
        positionReferenceText: {
            type: String,
            default: 'All Competition'
        },
        kpiOptions: {
            type: Array,
            default: () => KpiOption.options,
        }
    },
    components: {
        MissingDataText,
        RadioButton,
        SectionTitle,
        MoreInfo,
        RainbowLegend,
        LongitudinalChart
    },
    data() {
        return {
            sdUnit: getMeasurementUnit(MeasurementTypes.ShortDistance),
            countUnit: getMeasurementUnit(MeasurementTypes.Count),
            rainbowDataByKpi: null,
            rainbowDataByKpisByEpoch: null,
            peakEpochCountsByKpis: null,
            peakEpochCountsByKpisByEpoch: null,
            epochSelected: '_30S',
            epochOptions: [
                { text: '30s', value: '_30S' },
                { text: '60s', value: '_60S' },
                { text: '120s', value: '_120S' }
            ],
            title: '',
            Colours,
            hasPeakEpochData: false,
            hasPeakEpochCountData: false,
            kpiSelected: KpiOption.defaultSelection
        };
    },
    methods: {
        updateKpi(event) {
            this.kpiSelected = event;
        },
        getPeakData() {
            const generateRainbowData = kpiKey => {
                const playerPeakEpochs = this.rainbowData[kpiKey];
                if (playerPeakEpochs) {
                    const comparisonEpoch = this.rainbowComparisonData[kpiKey];
                    return {
                        _30S: generateKpiEpochData(this.sessions, playerPeakEpochs._30S, comparisonEpoch ? comparisonEpoch._30S : null),
                        _60S: generateKpiEpochData(this.sessions, playerPeakEpochs._60S, comparisonEpoch ? comparisonEpoch._60S : null),
                        _120S: generateKpiEpochData(this.sessions,playerPeakEpochs._120S, comparisonEpoch ? comparisonEpoch._120S : null)
                    };
                }
            };

            this.rainbowDataByKpi = {
                DISTANCE: generateRainbowData('DISTANCE'),
                HSR: generateRainbowData('HSR'),
                SPRINT: generateRainbowData('SPRINT'),
                TURNS: generateRainbowData('TURNS')
            };

            const generatePeakThresholdCounts = kpiKey => {
                const playerThresholds = this.peakEpochCountData[kpiKey];
                if (playerThresholds) {
                    const comparisonCounts = this.peakEpochCountComparisonData[kpiKey];
                    return {
                        _30S: mapKpiCategory(this.sessions, playerThresholds._30S, comparisonCounts ? comparisonCounts._30S : null),
                        _60S: mapKpiCategory(this.sessions, playerThresholds._60S, comparisonCounts ? comparisonCounts._60S : null),
                        _120S: mapKpiCategory(this.sessions, playerThresholds._120S, comparisonCounts ? comparisonCounts._120S : null)
                    };
                }
            };

            this.peakEpochCountsByKpis = {
                DISTANCE: generatePeakThresholdCounts('DISTANCE'),
                HSR: generatePeakThresholdCounts('HSR'),
                SPRINT: generatePeakThresholdCounts('SPRINT'),
                TURNS: generatePeakThresholdCounts('TURNS')
            };
        },
        updatePeakCapacity() {
            if (!this?.rainbowDataByKpi?.[this.kpiSelected]?.[this.epochSelected]) {
                this.hasPeakEpochData = false;
                return;
            }
            this.hasPeakEpochData = true;
            const unit = this.kpiSelected === 'TURNS' ? this.countUnit : this.sdUnit;
            const selectedKpi = this.rainbowDataByKpi[this.kpiSelected];
            const converted = {
                '_30S': convertRainbowChart(selectedKpi._30S, this.sessions, unit),
                '_60S': convertRainbowChart(selectedKpi._60S, this.sessions, unit),
                '_120S': convertRainbowChart(selectedKpi._120S, this.sessions, unit)
            };
            this.rainbowDataByKpisByEpoch = converted[this.epochSelected];
            
        },
        updatePeakCounts() {
            if (!this.peakEpochCountsByKpis || !this.peakEpochCountsByKpis[this.kpiSelected] || !this.peakEpochCountsByKpis[this.kpiSelected][this.epochSelected]) {
                this.hasPeakEpochCountData = false;
                return;
            }

            this.hasPeakEpochCountData = true;
            const selectedKpi = this.peakEpochCountsByKpis[this.kpiSelected];
            const converted = {
                '_30S': convertChart(selectedKpi._30S, this.sessions, this.countUnit),
                '_60S': convertChart(selectedKpi._60S, this.sessions, this.countUnit),
                '_120S': convertChart(selectedKpi._120S, this.sessions, this.countUnit)
            };
            this.peakEpochCountsByKpisByEpoch = converted[this.epochSelected];

        },
        updatePeakCharts() {
            this.updatePeakCapacity();
            this.updatePeakCounts();
            this.title = this.epochOptions.find(option => option.value === this.epochSelected)?.text || '';
        }
    },
    computed: {
        selectedKpiTitle() {
            return this.kpiOptions.find(t => t.value === this.kpiSelected)?.text || '';
        },
    },
    watch: {
        peakEpochData() {
            this.getPeakData();
            this.updatePeakCharts();
        },
        peakEpochCountData() {
            this.getPeakData();
            this.updatePeakCharts();
        }
    },
    mounted() {
        this.getPeakData();
        this.updatePeakCharts();
    }
};
</script>
